RivalIQ.module('graph', function (graph, App, Backbone, Marionette, $, _, d3) {

    'use strict';

    BarGraph.prototype = new graph.Graph();

    function BarGraph(options) {
        this.base = graph.Graph;
        this.base();
        this.maxRows = 30;
        this.excludedSubMetrics = [];
        _.extend(this, options);
        _.extend(this, Backbone.Events);
    }

    graph.BarGraph = BarGraph;

    // this is the padding from the very bottom of the SVG (y-position)
    BarGraph.LEGEND_PADDING_BOTTOM = -10;
    // this is the padding between the text and the next rectangle
    BarGraph.LEGEND_PADDING_LEFT = 10;
    // this is the width (and height) of the rectangle
    BarGraph.LEGEND_RECT_WIDTH = 16;
    // this is the amount of padding between the text and rectangle
    BarGraph.LEGEND_RECT_WIDTH_PADDING = 4;
    // this is the padding between the end of the bar and the label
    BarGraph.LABEL_PADDING_LEFT = 10;
    // this is the padding between the end of the bar and the label
    BarGraph.SECONDARY_CHANGE_LABEL_PADDING_LEFT = 5;
    // this is the metric name to use for the legend
    BarGraph.LEGEND_NAME_KEY = 'legendName';

    // If we're showing the benchmark line, this is how many vertical pixels to reserve for the name at the bottom of the line.
    BarGraph.BENCHMARK_NAME_PADDING_BOTTOM = 20;
    BarGraph.BENCHMARK_NAME_PADDING_TOP = 10;

    // this is the additional width to add to the triangle alerts
    BarGraph.NOT_ENOUGH_HISTORY_TEXT_ADDITIONAL_WIDTH = 40;

    BarGraph.MARGIN_LEFT = 0;

    const DIAGONAL_STROKE_WIDTH = 3;
    const DIAGONAL_INTER_STROKE_PADDING = 6;
    const HORIZONTAL_STROKE_WIDTH = 2;
    const HORIZONTAL_INTER_STROKE_PADDING = 2;

    const NO_DATA_LINE_HEIGHT = 20;
    const NO_DATA_LINE_WIDTH = 362;

    // BUG BUG this is the wrong place for this, is duplicative
    // w/ other formatters  and prevents us from sending other metrics
    // (such as abs. change) to this graph
    const percentFormatter = function (value) {
        return RivalIQ.formatter.absolutePercent(value, {
            minGoesToZero: true,
            trailingZeros: false,
            smallValueSignFcn: function (v) {
                // always return a plus. this works to produce +0% regardless of the direction of the
                // small value change.
                return '+';
            },
            signFcn: function (v) {
                return (v < 0 ? '' : '+');
            }
        });
    };

    BarGraph.prototype.setMaxRows = function (maxRows) {
        this.maxRows = maxRows;
    };

    /**
     * Returns a boolean indicating whether the submetric is excluded or not.
     * @param submetric
     * @returns {boolean}
     */
    BarGraph.prototype.isExcludedSubmetric = function (submetric) {

        const me = this;
        const metric = submetric.metric;
        return (me.excludedSubMetrics.indexOf(metric) >= 0);
    };

    /**
     * Handles the click event of stacked bar legends.
     * @param d
     */
    BarGraph.prototype.onStackedBarLegendClick = function (d) {
        const me = this;
        let event = null;
        const metric = d.metric;
        const index = me.excludedSubMetrics.indexOf(metric);

        if (index >= 0) {
            event = 'removed';
            me.excludedSubMetrics.splice(index, 1);
        } else {
            // NOTE because we may have to go back to the server we need to
            // preclude the client from allowing all submetrics to be excluded,
            // or we run the risk of erroneously displaying an empty message
            const canExclude = ((_.size(me.submetrics) - _.size(me.excludedSubMetrics)) > 1);
            if (canExclude) {
                event = 'added';
                me.excludedSubMetrics.push(metric);
            }
        }

        if (event) {
            me.trigger(`exclusion:${event}`, metric);
        }
    };

    /**
     * Gets the datum value.
     * @param d
     */
    BarGraph.prototype.getDatumValue = function (d) {

        const me = this;
        const value = d.value;

        // NOTE if the value is not a number then we should fall back to the
        // aggregate value, as that will trigger the empty data message,
        // otherwise we will assume there is enough data, and we should default
        // to zero instead of null (using the latter could cause the empty data
        // message to be triggered erroneously during filtering)
        if (_.get(d, 'submetrics') && me.hasSubmetrics && !isNaN(parseFloat(value))) {

            return _.reduce(d.submetrics, function (memo, submetric) {

                return (me.isExcludedSubmetric(submetric) ? memo : memo + submetric.value);
            }, 0);
        }

        return value;
    };

    BarGraph.prototype.onAttachLoaded = function () {
        const me = this;
        me.renderLegend();
    };

    BarGraph.prototype.renderLegend = function () {
        const me = this;

        const legendOffset = me.innerHeight + me.margin.bottom + BarGraph.LEGEND_PADDING_BOTTOM;

        let submetrics = me.submetrics;
        if (me.pruneLegend) {
            submetrics = _.reject(submetrics, function (submetric) {
                return me.isExcludedSubmetric(submetric);
            });
        }

        const legend = me.svg.selectAll('.legend')
            .data([submetrics])
            .enter().append('g')
            .attr('transform', `translate(${10},${legendOffset})`)
            .attr('class', 'legend');

        const legendItem = legend.selectAll('.legend-item')
            .data(function (d) { return d; })
            .enter().append('g')
            .attr('class', 'legend-item');

        legendItem
            .append('rect')
            .attr('y', -0.5 * BarGraph.LEGEND_RECT_WIDTH)
            .attr('height', function (d) {
                return (me.isExcludedSubmetric(d) ? BarGraph.LEGEND_RECT_WIDTH - 1 : BarGraph.LEGEND_RECT_WIDTH);
            })
            .attr('width', function (d) {
                return (me.isExcludedSubmetric(d) ? BarGraph.LEGEND_RECT_WIDTH - 1 : BarGraph.LEGEND_RECT_WIDTH);
            })
            .attr('class', function (d) {
                const classNames = [d.class, 'pointer'];

                if (me.isExcludedSubmetric(d)) {
                    classNames.push(graph.Graph.EXCLUDED_LEGEND_CLASS_NAME);
                }

                return classNames.join(' ');
            })
            .on('click', _.bind(me.onStackedBarLegendClick, me))
            .each(function (d, idx) {
                const node = this;

                if (me.renderBarPatterns) {
                    if (idx === 1) {
                        me.renderHorizontalBarPattern(node, d3.select(node.parentNode));
                    } else if (idx === 2) {
                        me.renderDiagonalBarPattern(node, d3.select(node.parentNode));
                    }
                }
            });

        legendItem
            .append('text')
            .attr('font-family', 'FontAwesome')
            .attr('class', 'legend-checkbox')
            .attr('y', 4.5)
            .attr('x', 1.5)
            .text('\uf00c'); // @fa-var-check

        legendItem
            .append('text')
            .attr('y', 0.25 * BarGraph.LEGEND_RECT_WIDTH)
            .attr('x', 16)
            .attr('class', function (d) {
                const classNames = ['x-legend-title', 'pointer'];

                if (me.isExcludedSubmetric(d)) {
                    classNames.push(graph.Graph.EXCLUDED_LEGEND_CLASS_NAME);
                }

                return classNames.join(' ');
            })
            .attr('id', function (d, i) {

                return `legend-${me.graphID}-${i}`;
            })
            .html(function (d) {
                // These spaces are to give some space between the legend name; if we do this via spacing in `x`,
                // we'll end up with a dead area left of the legend key where we don't get click events.
                return `&nbsp;&nbsp;${d[BarGraph.LEGEND_NAME_KEY]}`;
            })
            .on('click', _.bind(me.onStackedBarLegendClick, me));

        let width = 0;
        legendItem
            .attr('transform', function (d, i) {
                const translation = `translate(${width + (i * BarGraph.LEGEND_PADDING_LEFT)}, 0)`;
                width += this.getBBox().width;

                return translation;
            });
    };

    BarGraph.prototype.renderBenchmarkLine = function (graph, x, gridChartOffset, yScale) {
        const me = this;

        if (!me.showBenchmarkLine || !me.benchmarkData) {
            return;
        }

        const xPosition = x(me.benchmarkData.rawValue);
        graph.append('line')
            .attr('x1', xPosition)
            .attr('x2', xPosition)
            .attr('y1', -gridChartOffset)
            .attr('y2', yScale.rangeExtent()[1] + gridChartOffset)
            .attr('class', 'benchmark-line')
            .style('stroke', '#bbb');

        const name = me.benchmarkData.name;
        const textContainer = graph.append('g');
        const text = textContainer.append('text')
            .attr('y', yScale.rangeExtent()[1] + gridChartOffset + BarGraph.BENCHMARK_NAME_PADDING_TOP)
            .attr('stroke', 'none')
            .attr('text-anchor', 'start')
            .attr('class', 'benchmark-line-name')
            .text(name);

        if (me.benchmarkData.notAvailableTooltip) {
            const isWarningTriangle = !_.isNull(me.benchmarkData.rawValue);
            const warningIcon = textContainer.append('text')
                .attr('font-family', 'FontAwesome')
                .attr('height', 10)
                .attr('width', 10)
                .attr('y', yScale.rangeExtent()[1] + gridChartOffset + BarGraph.BENCHMARK_NAME_PADDING_TOP)
                .attr('x', text.node().getBBox().width + 5)
                .text(isWarningTriangle ? '\uf071' : '--');

            if (isWarningTriangle) {
                warningIcon.attr('class', 'x-not-enough-history-icon');
            }

            App.utils.tooltip.addPopover($(warningIcon.node()), me.benchmarkData.notAvailableTooltip, {
                placement: 'top'
            });
        }

        if (me.benchmarkData.description) {
            App.utils.tooltip.addTooltip($('.benchmark-line-name'), {
                title: me.benchmarkData.description,
                placement: 'top',
                container: 'body'
            });
        }

        const textBBox = textContainer.node().getBBox();
        const idealXPosition = xPosition - textBBox.width / 2;

        textContainer.attr('transform', `translate(${idealXPosition},5)`);
    };

    // Though it would be cleaner and probably a little more efficient to use svg patterns for these hatches, there's a long
    // standing bug in Chrome+Skia which results in these patterns looking terrible in PDFs. So, we get to render these lines
    // by hand.
    BarGraph.prototype.renderHorizontalBarPattern = function (rect, parentNode) {
        const bbox = rect.getBBox();

        const linesContainer = parentNode.append('g');
        linesContainer
            .attr('x', bbox.x)
            .attr('y', bbox.y)
            .attr('width', bbox.width)
            .attr('height', bbox.height);

        for (let y = bbox.y + HORIZONTAL_INTER_STROKE_PADDING; y <= bbox.y + bbox.height; y += HORIZONTAL_STROKE_WIDTH + HORIZONTAL_INTER_STROKE_PADDING) {

            const x1 = bbox.x;
            const x2 = bbox.width + bbox.x;

            linesContainer
                .append('line')
                .attr('class', 'bar-patterns-line bar-pattern-line-horizontal')
                .attr('x1', x1)
                .attr('y1', y)
                .attr('x2', x2)
                .attr('y2', y);
        }
    };

    BarGraph.prototype.renderDiagonalBarPattern = function (rect, parentNode) {
        const bbox = rect.getBBox();

        const linesContainer = parentNode.append('g');
        linesContainer
            .attr('x', bbox.x)
            .attr('y', bbox.y)
            .attr('width', bbox.width)
            .attr('height', bbox.height);

        // Height sometimes appears in computations for x coordinates below because we're drawing lines with
        // slope 1.
        for (let i = 0; i < bbox.width + bbox.height; i += DIAGONAL_STROKE_WIDTH + DIAGONAL_INTER_STROKE_PADDING) {
            const x2 = bbox.x + i;
            const y2 = bbox.y; // top of box

            const x1 = x2 - bbox.height;
            const y1 = bbox.y + bbox.height; // bottom of box

            linesContainer
                .append('line')
                .attr('class', 'bar-patterns-line')
                .attr('x1', x1)
                .attr('y1', y1)
                .attr('x2', x2)
                .attr('y2', y2);
        }

        const containerBBox = linesContainer.node().getBBox();
        const insetX1 = bbox.height;
        const insetX2 = containerBBox.width - bbox.width - bbox.height;

        linesContainer.attr('style', `clip-path: inset(0px ${insetX2}px 0px ${insetX1}px)`);
    };

    BarGraph.prototype.render = function () {
        const me = this;

        // TODO This is not idiomatic d3, is inefficient and prevents us from
        // animating
        this.clearRendering();
        const isReadOnly = RivalIQ.state.user.isReadOnly();
        const gridChartOffset = 3;
        BarGraph.MARGIN_LEFT = this.margin.left;
        const width = this.width - this.margin.right - this.margin.left;

        me.innerHeight = this.height - this.margin.top - this.margin.bottom;
        let graphHeight = me.innerHeight;
        if (me.showBenchmarkLine) {
            // If we're going to show that benchmark name, we need to steal some height from the graph.
            graphHeight -= BarGraph.BENCHMARK_NAME_PADDING_BOTTOM + BarGraph.BENCHMARK_NAME_PADDING_TOP;
        }

        const format = this.formatX;

        const x = d3.scale.linear()
            .range([0, width]);
        let yScale;
        if (this.data.length > 20) {
            yScale = d3.scale.ordinal()
                .rangeBands([0, graphHeight], 0.5, 0.5);
        } else {
            yScale = d3.scale.ordinal()
                .rangeRoundBands([0, graphHeight], 0.5);
        }
        const y = function (d, i) {
            return yScale(i);
        };
        d3.svg.axis()
            .scale(x)
            .orient('bottom')
            .tickSize(2)
            .tickFormat(format)
            .ticks(this.axisTicks);

        me.svg = d3.select(this.target).append('svg')
            .attr('width', width + this.margin.right + this.margin.left)
            .attr('height', me.innerHeight + this.margin.top + this.margin.bottom);

        const graph = me.graph = me.svg.append('g')
            .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

        if (me.data.length === 0 && me.noDataHtmlMessage) {
            const lines = _.trim(me.noDataHtmlMessage).split('\n');
            const xOffset = (width / 2 + NO_DATA_LINE_WIDTH / 2);
            const text = me.svg
                .append('text')
                .attr('transform', `translate(${xOffset}, ${me.innerHeight / 2 - NO_DATA_LINE_HEIGHT * lines.length / 2})`)
                .attr('class', 'no-data-msg');

            _.each(lines, function (line, lineIdx) {
                line = _.trim(line);
                text.append('tspan')
                    .attr('width', NO_DATA_LINE_WIDTH)
                    .attr('height', NO_DATA_LINE_HEIGHT)
                    .attr('text-anchor', 'middle')
                    .attr('x', 0)
                    .attr('y', lineIdx * NO_DATA_LINE_HEIGHT)
                    .text(line);
            });
            return;
        }

        const dataToPlot = this.data.slice(0, this.data.length);

        // Set the scale domain.
        let xmin = Math.min(0, d3.min(dataToPlot, function (d) {

            const value = me.getDatumValue(d);
            return (value ? value : 0);
        }));

        let xmax = Math.max(0, d3.max(dataToPlot, function (d) {

            const value = me.getDatumValue(d);
            return (value ? value : 0);
        }));

        // Need to look at the benchmark too; if bars are hidden via exclusions, the benchmark value might be the largest or smallest value.
        if (me.showBenchmarkLine) {
            xmax = Math.max(me.benchmarkData.rawValue, xmax);
            xmin = Math.min(me.benchmarkData.rawValue, xmin);
        }

        if (xmin < 0) {
            xmin = xmin - Math.abs(xmin) * 0.2;
            xmax = xmax + Math.abs(xmax) * 0.2;
        }

        x.domain([xmin, xmax]);

        yScale.domain(dataToPlot.map(function (d, i) {
            return i;
        }));

        const yText = function (d, i) {
            return y(d, i) + yScale.rangeBand() / 2;
        };

        me.renderBenchmarkLine(graph, x, gridChartOffset, yScale);

        // Draw the vertical line at 0.
        graph.append('line')
            .attr('class', 'axis')
            .attr('x1', x(0))
            .attr('x2', x(0))
            .attr('y1', -gridChartOffset)
            .attr('y2', graphHeight + gridChartOffset);

        // a general wrapper for each bar
        const bar = graph.selectAll('g.bar')
            .data(dataToPlot)
            .enter().append('g')
            .attr('class', function (d) {
                const c = ['bar'];

                if (d.focus) {
                    c.push('focus');
                }

                return c.join(' ');
            });

        // throw the bar labels on there
        bar.append('text')
            .attr('y', yText)
            .attr('stroke', 'none')
            .attr('fill', function (d) {

                return (me.getDatumValue(d) === void(0) ? '#888' : '#4c535e');
            })
            .attr('font-weight', function (d) {

                return (d.focus ? 'bold' : 'normal');
            })
            .attr('transform', 'translate(-5,0)')
            .attr('dy', '.35em') // vertical-align: middle
            .attr('text-anchor', 'end')
            .attr('class', function (d) {

                const c = ['x-graph-y-axis-label'];

                if (me.getDatumValue(d) === void(0)) {
                    if (!d.disabled) {
                        c.push('pointer');
                    }
                    c.push('x-elision');

                } else {
                    c.push(`${isReadOnly ? '' : 'pointer '}x-company-name`);
                }

                return c.join(' ');
            })
            .text(function (d) {

                let maxOrdinalLength = (window.innerWidth < 480 ? 10 : 24);
                if (me.isExportingPDF) {
                    maxOrdinalLength = Math.min(maxOrdinalLength, 20);
                }
                return me.trimOrdinalLabel(d.name, maxOrdinalLength);
            });

        // the bars themselves
        if (me.hasSubmetrics) {

            const wrapper = bar.append('g')
                .attr('class', 'g')
                .attr('transform', function (d, i) {

                    return `translate(0,${y(d, i)})`;
                });

            wrapper.selectAll('rect')
                .data(function (d) {

                    let offset = 0;
                    // NOTE we need to calculate the x-axis locations here as
                    // we will be filtering channels in and out based on what
                    // is in the list of exclusions
                    // NOTE if the aggregate value isn't a number, then don't
                    // include the submetrics
                    if (isNaN(parseFloat(d.value))) {
                        return [];
                    } else {
                        const data = _.reduce(d.submetrics, function (memo, submetric, metricIdx) {

                            let included = null;

                            if (!me.isExcludedSubmetric(submetric)) {

                                included = _.extend({}, submetric, {
                                    x0: offset,
                                    x1: offset += submetric.value,
                                    metricIdx: metricIdx
                                });

                                memo.push(included);
                            }

                            return memo;
                        }, []);

                        // the last submetric now holds the cumulative sum of
                        // all the submetrics' values
                        // NOTE we can get an empty array if all of the
                        // channels have been removed (i.e., we will still
                        // have a total value, but the submetrics will be
                        // elided yielding an empty array in `data`)
                        const total = _.get(_.last(data), 'x1');
                        _.forEach(data, function (datum) {
                            datum.percentOfTotal = (total ? datum.value / total : 0);
                        });

                        return data;
                    }
                })
                .enter().append('rect')
                .attr('height', yScale.rangeBand())
                .attr('x', function (d) {
                    return x(d.x0);
                })
                .attr('class', function (d) {

                    return d.class;
                })
                .attr('width', function (d) {
                    return x(d.x1) - x(d.x0);
                })
                .each(function (d) {
                    const node = this;

                    if (me.renderBarPatterns) {
                        if (d.metricIdx === 1) {
                            me.renderHorizontalBarPattern(node, d3.select(node.parentNode));
                        } else if (d.metricIdx === 2) {
                            me.renderDiagonalBarPattern(node, d3.select(node.parentNode));
                        }
                    }
                });
        } else {

            bar.append('rect')
                // add initial translation so the real transform won't throw up
                // see http://stackoverflow.com/a/23871071 for more information
                .attr('transform', 'translate(0,0)')
                .attr('class', function (d) {
                    let clss = 'x-graph-bar';
                    if (d.focus) {
                        clss += ' x-focus';
                    }
                    return clss;
                })
                .attr('y', y)
                .attr('height', yScale.rangeBand())
                .attr('transform', function (d) {

                    let val = me.getDatumValue(d);

                    if (val === null || val === void(0)) {

                        val = 0;
                    }

                    const w = Math.abs(x(val) - x(0));
                    const dtz = x(0);
                    let transd = Math.abs(w - dtz);

                    if (val >= 0) {

                        transd = x(0);
                    }

                    return `translate(${transd},0)`;
                })
                .attr('width', function (d) {

                    let val = me.getDatumValue(d);

                    if (val === null || val === void(0)) {

                        val = 0;
                    }

                    return Math.abs(x(val) - x(0));
                });
        }

        // the labels that go onto the end of the bar to show the values

        const nehText = bar.append('g')
            .attr('transform', function (d, i) {
                const y = yText(d, i) + 5;
                return `translate(${10}, ${y})`;
            })
            .attr('class', function (d) {
                if (_.isNull(d.value)) {
                    return 'x-no-data-found';
                }
                return '';
            })
            .attr('data-toggle', function (d) {
                if (_.isNull(d.value)) {
                    return 'popover';
                }
                return '';
            })
            .attr('data-content', function (d) {
                let dataContent = '';
                if (d.noDataTitle) {
                    if (d.noDataUnboldTitle) {
                        dataContent += `<p class='no-data-title'>${d.noDataTitle}</p>`;
                    } else {
                        dataContent += `<p class='no-data-title-bold'>${d.noDataTitle}</p>`;
                    }
                }
                if (d.noDataDescription) {
                    dataContent += `<p class='no-data-description'>${d.noDataDescription}</p>`;
                }
                return dataContent;
            });

        // put a white background behind the neh text to avoid the odd look when the vertical axis goes through the warning.
        nehText.append('rect')
            .attr('class', 'neh-background')
            .attr('height', 20)
            .attr('y', -15)
            .attr('width', function (d) {
                if (d.noDataMessage) {
                    const textWidth = RivalIQ.graph.utils.getTextBoundingBox(d.noDataMessage).width + BarGraph.NOT_ENOUGH_HISTORY_TEXT_ADDITIONAL_WIDTH;
                    return textWidth;
                } else {
                    return 0;
                }
            })
            .attr('style', function (d) {
                if (_.isNull(d.value)) {
                    return 'fill: white !important';
                }
                return 'fill: none !important';
            });

        nehText.append('text')
            .attr('font-family', 'FontAwesome')
            .attr('height', 10)
            .attr('width', 10)
            .text(function (d) {
                if (_.isNull(d.value)) {
                    return '--';
                }
            });

        nehText.append('text')
            .attr('x', 15)
            .attr('class', 'x-not-enough-history-text')
            .attr('width', function (d) {
                if (_.isNull(d.value)) {
                    // width of the width left over after the company name and line
                    const parentWidth = $(this).closest('svg').width() - BarGraph.MARGIN_LEFT;
                    const textWidth = RivalIQ.graph.utils.getTextBoundingBox(d.noDataMessage).width + BarGraph.NOT_ENOUGH_HISTORY_TEXT_ADDITIONAL_WIDTH;
                    return (parentWidth < textWidth ? parentWidth : textWidth);
                }
                return 0;
            })
            .attr('height', function (d) {
                if (_.isNull(d.value)) {
                    // width of the width left over after the company name and line
                    const parentWidth = $(this).closest('svg').width() - BarGraph.MARGIN_LEFT;
                    const textWidth = RivalIQ.graph.utils.getTextBoundingBox(d.noDataMessage).width + BarGraph.NOT_ENOUGH_HISTORY_TEXT_ADDITIONAL_WIDTH;
                    // double the height if the width is shortened
                    return (parentWidth < textWidth ? 40 : 20);
                }
                return 20;
            })
            .text(function (d) {
                if (_.isNull(d.value)) {
                    return d.noDataMessage;
                }
                return '';
            });

        const barLabel = bar.append('text')
            .attr('y', yText)
            .attr('dx', -3)
            .attr('dy', '.35em')
            .attr('text-anchor', 'start')
            .attr('x', function (d) {

                // now that we have the text, get its length and save it away in case we need to stick the
                // % change at the end
                d.textWidth = this.getComputedTextLength();
                let val = me.getDatumValue(d);

                if (val === undefined || val === null || val < 0) {
                    val = 0;
                }

                return x(val) + BarGraph.LABEL_PADDING_LEFT;
            });

        barLabel.append('tspan')
            .attr('class', function (d) {
                let c = 'value-outside bar-value-label';

                if (d.focus) {
                    c += ' x-focus';
                }
                return c;
            })
            .text(function (d) {
                const value = me.getDatumValue(d);

                let label = '';
                if (_.isNull(value) || typeof value === 'undefined') {

                    label = '';
                } else {

                    // build the current value label
                    label = format(value);

                }
                return label;

            });

        // render the % change numbers if necessary
        barLabel.append('tspan')
            .attr('class', function (d) {

                let c = 'change-detail ';
                if (d.secondary > 0) {
                    c += 'pos';
                } else if (d.secondary < 0) {
                    c += 'neg';
                }

                return c;
            })
            .attr('dx', 5)
            .text(function (d) {

                const value = me.getDatumValue(d);
                let label = '';
                if (value !== null && typeof value !== 'undefined' && d.secondary !== null && typeof d.secondary !== 'undefined') {

                    label = percentFormatter(d.secondary);
                }

                return label;

            });

        // render any partial data warning triangles and text
        barLabel.append('tspan')
            .attr('font-family', 'FontAwesome')
            .attr('class', 'x-partial-data-warning')
            .attr('height', 10)
            .attr('width', 10)
            .attr('dx', 5)
            .text(function (d) {
                if (d.partialDataWarning) {
                    const value = me.getDatumValue(d);
                    if (value !== null && d.secondary === null && d.showNullSecondaryWarning) {
                        return '--';
                    } else {
                        return '\uf071';
                    }
                }
            })
            .attr('data-toggle', function (d) {
                if (d.partialDataWarning) {
                    return 'popover';
                }
                return '';
            })
            .attr('data-content', function (d) {
                return _.get(d, ['partialDataWarning', 'popover', 'content']);
            });

        let tooltip = d3.select('div.tooltip');

        if (!$('div.tooltip').length) {
            tooltip = d3.select('body').append('div')
                .attr('class', 'tooltip x-graph-tooltip hide')
                .style('opacity', 0);
        }

        let isOver;

        const hideTooltipEl = function () {
            tooltip.transition().duration(500).style('opacity', 0);
            tooltip.attr('class', 'tooltip x-graph-tooltip hide');
            isOver = null;
        };

        // now that we have stacked bars we need to do something a little
        // bit different to identify data. with stacked bars each rectangle
        // (section) has the same id, which is why we should use class and id
        // instead (it is unique for each rectangle/section and bar)
        const getDataId = function (d) {
            return d.class ? d.class + d.id : d.id;
        };

        const hideTooltip = function (d) {
            const timeout = setTimeout(function () {
                if (isOver === getDataId(d)) {
                    hideTooltipEl();
                }
            }, 300);

            // this is how we enable sticky tooltips that don't disappear if you
            // mouseover them, and do disappear once you leave
            $(tooltip[0])
                .off('mouseenter')
                .off('mouseleave')
                .one('mouseenter', function () {
                    clearTimeout(timeout);
                })
                .one('mouseleave', hideTooltipEl);
        };

        let tooltipSelection = bar.selectAll('rect:not(.neh-background)');
        if (me.hasSubmetrics) {
            tooltipSelection = bar.selectAll('rect:not(.neh-background), .bar-value-label');
        }

        tooltipSelection.on('mouseover', function (d) {

            if (isOver !== getDataId(d)) {
                const tooltipTemplate = Marionette.TemplateCache.get('#metricChartTooltip');

                d3.select('#tooltipContainer').remove();

                tooltip = d3.select('body')
                    .append('div')
                    .attr('id', 'tooltipContainer')
                    .attr('class', 'popover fade bottom in')
                    .style('display', 'block');
                const content = tooltip.append('div');

                const tooltipContentData = {
                    className: null,
                    name: d.companyName || d.name,
                    value: me.formatForPopover(me.getDatumValue(d)),
                    percentage: null,
                    showSubmetric: me.hasSubmetrics && d.percentOfTotal,
                    secondary: null,
                    secondaryClass: null,
                    isContentLinkable: me.isContentLinkable
                };

                if (me.hasSubmetrics && !_.isEmpty(d.submetrics)) {
                    tooltipContentData.submetricData = _.map(d.submetrics, function (submetric, submetricIdx) {

                        const legendItemClass = _.compact([
                            submetric.class,
                            `legend-item-${submetricIdx + 1}`,
                            (me.renderBarPatterns ? 'bar-pattern-legend-item' : ''),
                            (d.id === me.focusCompanyId ? 'focus-company' : '')
                        ]).join(' ');

                        return {
                            legendItemClass: legendItemClass,
                            key: submetric.tooltipName,
                            value: me.formatForPopover(submetric.value)
                        };
                    });
                }

                if (me.hasSubmetrics && d.percentOfTotal) {
                    tooltipContentData.percentage = `(${RivalIQ.formatter.formatPercentage(d.percentOfTotal)} of total)`;
                    tooltipContentData.metricName = d.tooltipName;
                    tooltipContentData.submetricClass = [
                        d.metric,
                        `legend-item-${d.metricIdx + 1}`,
                        (me.renderBarPatterns ? ' bar-pattern-legend-item' : ''),
                        (d.id === me.focusCompanyId ? 'focus-company' : '')
                    ].join(' ');
                } else {
                    if (d.secondary !== null && typeof d.secondary !== 'undefined') {
                        tooltipContentData.secondary = percentFormatter(d.secondary);
                        if (d.secondary > 0) {
                            tooltipContentData.secondaryClass = 'delta-rel-positive';
                        } else if (d.secondary < 0) {
                            tooltipContentData.secondaryClass = 'delta-rel-negative';
                        } else {
                            tooltipContentData.secondaryClass = 'delta-rel-neutral';
                        }
                    }
                }

                content.html(tooltipTemplate(tooltipContentData));

                const width = parseInt(tooltip.style('width'));
                tooltip
                    .style('left', `${d3.event.pageX + 5 - width / 2}px`)
                    .style('top', `${d3.event.pageY}px`);

                isOver = getDataId(d);
            }

            // if this is something that can link to content then we need to
            // add a click listener so that we can properly instantiate the
            // modal content view
            if (me.isContentLinkable) {
                $(tooltip[0]).find('.x-view-content').off('click').one('click', function () {
                    const company = _.extend({}, d);

                    // we need to check to see if there is a property called companyName
                    // and set the name property so that the content modal creation will
                    // work correctly. as of now we need to do this for stacked bars only...
                    if (company.companyName) {
                        company.name = company.companyName;
                    }

                    me.trigger('analyze-content', company);
                    hideTooltipEl();
                    return false;
                });
            }
        }).on('mouseout', hideTooltip);

        const companyClick = function (d) {
            window.location = `/company/${d.id}`;
            return window.location;
        };

        const elisionClick = function (d) {
            me.trigger('inclusion', d.id);
        };

        App.utils.tooltip.addTooltip($('.y.axis text'), {
            title: 'Click to view company profile',
            placement: 'right'
        });

        if (!isReadOnly) {
            graph.selectAll('.x-graph-y-axis-label.x-company-name').on('click', companyClick);
        }
        graph.selectAll('.x-graph-y-axis-label.x-elision.pointer').on('click', elisionClick);

        // display not enough history popover
        graph.selectAll('.x-no-data-found, .x-partial-data-warning')[0].forEach(function (popover) {
            $(popover).popover({
                html: true,
                trigger: 'manual',
                placement: 'auto',
                container: 'body'
            })
                .on('mouseenter', function () { // hack to keep the popover alive in svg
                    const _this = this;
                    $(this).popover('show');
                    $('.popover').on('mouseleave', function () {
                        $(_this).popover('hide');
                    });
                    // hide on popovers when a link is clicked
                    $('.popover a').on('click', function () {
                        $('.popover').popover('hide');
                    });
                })
                .on('mouseleave', function () {
                    const _this = this;
                    setTimeout(function () {
                        if (!$('.popover:hover').length) {
                            $(_this).popover('hide');
                        }
                    }, 100);
                });
        });
    };

}, window.d3);
